// store/slices/tutorialSlice.js - Tutorial/Onboarding state slice

import api from '../../utils/api';

// Tutorial progress lives in localStorage, scoped per user id so that two
// accounts sharing one browser each get their own first-time experience.
const tutorialStorageKey = (base, userId) => {
  if (userId === undefined || userId === null) {
    throw new Error(`Tutorial storage key '${base}' requested with no logged-in user id`);
  }
  return `${base}_${userId}`;
};

export const tutorialCompletedKey = (userId) => tutorialStorageKey('gchain_tutorial_completed', userId);
export const tutorialPausedStepKey = (userId) => tutorialStorageKey('gchain_tutorial_paused_step', userId);

export const createTutorialSlice = (set, get) => {
  const completedKey = () => tutorialCompletedKey(get().user?.id);
  const pausedStepKey = () => tutorialPausedStepKey(get().user?.id);

  return {
  // State
  showWelcomeModal: false,
  showTutorial: false,
  tutorialStep: 0,
  hasCompletedTutorial: false,
  tutorialPaused: false,
  pausedAtStep: null,

  // Track which tutorial actions have been completed (reset on tutorial start)
  tutorialActions: {
    viewportChanged: false,
    nodeMoved: false,
    nodeCopied: false,
    nodePasted: false,
    undoPerformed: false,
    paramEdited: false,
    contextMenuOpened: false,
    catalogHovered: false,
    datasheetViewed: false,
    miniPlotClicked: false,
    findSimilarClicked: false,
    partReplaced: false,
    voltageChanged: false,
    smithChartClicked: false,
    chainModeEnabled: false,
    compressionTriggered: false,
    sweepModeEnabled: false,
    pdfExported: false,
    feedbackOpened: false
  },

  // Actions
  setShowWelcomeModal: (show) => set({ showWelcomeModal: show }),

  startTutorial: () => {
    // Clear any paused state when starting fresh
    localStorage.removeItem(pausedStepKey());
    api.track('event', 'tutorial_start');
    set({
      showWelcomeModal: false,
      showTutorial: true,
      tutorialStep: 0,
      tutorialPaused: false,
      pausedAtStep: null,
      chainMode: false,  // Disable chain mode for tutorial
      frequencyMode: 'single',  // Default to single frequency mode for tutorial
      // Reset all tutorial action flags
      tutorialActions: {
        viewportChanged: false,
        nodeMoved: false,
        nodeCopied: false,
        nodePasted: false,
        undoPerformed: false,
        paramEdited: false,
        contextMenuOpened: false,
        catalogHovered: false,
        datasheetViewed: false,
        miniPlotClicked: false,
        findSimilarClicked: false,
        partReplaced: false,
        voltageChanged: false,
        smithChartClicked: false,
        chainModeEnabled: false,
        compressionTriggered: false,
        sweepModeEnabled: false,
        pdfExported: false,
        feedbackOpened: false
      }
    });
  },

  skipTutorial: () => {
    localStorage.setItem(completedKey(), 'true');
    localStorage.removeItem(pausedStepKey());
    api.track('event', 'tutorial_skip');
    set({
      showWelcomeModal: false,
      showTutorial: false,
      hasCompletedTutorial: true,
      tutorialPaused: false,
      pausedAtStep: null
    });
  },

  pauseTutorial: () => {
    const currentStep = get().tutorialStep;
    localStorage.setItem(pausedStepKey(), String(currentStep));
    api.track('event', 'tutorial_pause', `step ${currentStep + 1}`);
    set({
      showTutorial: false,
      tutorialPaused: true,
      pausedAtStep: currentStep
    });
  },

  resumeTutorial: () => {
    const pausedStep = localStorage.getItem(pausedStepKey());
    const step = pausedStep ? parseInt(pausedStep, 10) : 0;
    set({
      showTutorial: true,
      tutorialStep: step,
      tutorialPaused: false,
      chainMode: false  // Disable chain mode for tutorial
    });
  },

  canResumeTutorial: () => {
    return localStorage.getItem(pausedStepKey()) !== null;
  },

  getPausedStep: () => {
    const pausedStep = localStorage.getItem(pausedStepKey());
    return pausedStep ? parseInt(pausedStep, 10) : null;
  },

  nextTutorialStep: () => set((state) => ({
    tutorialStep: state.tutorialStep + 1
  })),

  previousTutorialStep: () => set((state) => ({
    tutorialStep: Math.max(0, state.tutorialStep - 1)
  })),

  completeTutorial: () => {
    localStorage.setItem(completedKey(), 'true');
    localStorage.removeItem(pausedStepKey());
    api.track('event', 'tutorial_complete');
    set({
      showTutorial: false,
      hasCompletedTutorial: true,
      tutorialPaused: false,
      pausedAtStep: null
    });
  },

  checkFirstTimeUser: () => {
    const hasCompleted = localStorage.getItem(completedKey());
    const hasPaused = localStorage.getItem(pausedStepKey());

    if (!hasCompleted && !hasPaused) {
      set({ showWelcomeModal: true });
    } else if (hasPaused) {
      // User has a paused tutorial - set the flag so UI can show resume option
      set({
        tutorialPaused: true,
        pausedAtStep: parseInt(hasPaused, 10)
      });
    }
  },

  // Mark a tutorial action as completed
  completeTutorialAction: (actionName) => {
    const { showTutorial, tutorialActions } = get();
    if (!showTutorial) return; // Only track during active tutorial

    if (tutorialActions.hasOwnProperty(actionName) && !tutorialActions[actionName]) {
      set({
        tutorialActions: {
          ...tutorialActions,
          [actionName]: true
        }
      });
    }
  },
  };
};
